import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { packagingTaskService, productionOrderService } from '../services/api';
import { orderRefFromRow } from '../utils/orderLabel';

const DESTINATION_WAREHOUSES = [
  'Склад для пакування',
  'Склад готової продукції',
  'Склад сировини',
  'Склад упаковки',
  'Склад барвників',
  'Склад техматеріалів',
];

function formatArticleName(article, name) {
  const a = typeof article === 'string' ? article.trim() : '';
  const n = typeof name === 'string' ? name.trim() : '';
  if (a && n) return `${a} — ${n}`;
  if (a) return a;
  return n || '—';
}

function PackagingPage({ currentUser }) {
  const [semiItems, setSemiItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [startQty, setStartQty] = useState('');
  const [startWarehouse, setStartWarehouse] = useState('Склад готової продукції');
  const [saving, setSaving] = useState(false);
  const [completeDialog, setCompleteDialog] = useState(null);
  const [completeQty, setCompleteQty] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [semiRes, tasksRes, reqRes] = await Promise.all([
        packagingTaskService.semiStockItems(),
        packagingTaskService.list(),
        productionOrderService.packagingRequests(),
      ]);
      const semi = semiRes.data;
      setSemiItems(Array.isArray(semi) ? semi : semi?.results || []);
      const t = tasksRes.data;
      setTasks(Array.isArray(t) ? t : t?.results || []);
      const r = reqRes.data;
      setRequests(Array.isArray(r) ? r : r?.results || []);
    } catch (e) {
      console.error(e);
      alert('Помилка завантаження даних упаковки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openStart = (item) => {
    setSelectedItem(item);
    setStartQty(item.quantity > 0 ? String(item.quantity) : '');
    setStartWarehouse('Склад готової продукції');
    setStartOpen(true);
  };

  const openStartFromRequest = (req) => {
    const productId = Number(req?.product);
    const stockItem = semiItems.find((x) => Number(x.product) === productId) || null;
    if (!stockItem) {
      alert('Не знайдено позицію складу для цього виробу (немає StockItem з виробом).');
      return;
    }
    openStart({
      ...stockItem,
      production_order_id: req.id,
      production_order_packaging_note: req.packaging_note || '',
    });
  };

  const handleStart = async () => {
    if (!selectedItem) return;
    const qty = parseFloat(String(startQty).replace(',', '.'));
    if (!Number.isFinite(qty) || qty < 0.01) {
      alert('Вкажіть коректну кількість (мін. 0,01)');
      return;
    }
    if (!startWarehouse) {
      alert('Оберіть склад призначення');
      return;
    }
    setSaving(true);
    try {
      const poId = selectedItem?.production_order_id || null;
      await packagingTaskService.start({
        source_stock_item: selectedItem.id,
        quantity_taken: qty,
        destination_warehouse: startWarehouse,
        production_order: poId,
      });
      setStartOpen(false);
      setSelectedItem(null);
      await loadData();
    } catch (e) {
      const d = e.response?.data;
      alert(d ? JSON.stringify(d, null, 2) : e.message || 'Помилка');
    } finally {
      setSaving(false);
    }
  };

  const savePackagedDraft = async (task, valueStr) => {
    const v = parseFloat(String(valueStr).replace(',', '.'));
    if (!Number.isFinite(v) || v < 0.01) {
      alert('Кількість упакованого має бути не менше 0,01');
      return;
    }
    if (v > task.quantity_taken + 1e-9) {
      alert('Не більше ніж знято зі складу');
      return;
    }
    try {
      await packagingTaskService.patch(task.id, { quantity_packaged: v });
      await loadData();
    } catch (e) {
      const d = e.response?.data;
      alert(d ? JSON.stringify(d, null, 2) : e.message || 'Помилка збереження');
    }
  };

  const openComplete = (task) => {
    const draft =
      task.quantity_packaged != null ? String(task.quantity_packaged) : String(task.quantity_taken);
    setCompleteQty(draft);
    setCompleteDialog(task);
  };

  const handleComplete = async () => {
    if (!completeDialog) return;
    const v = parseFloat(String(completeQty).replace(',', '.'));
    if (!Number.isFinite(v) || v < 0.01) {
      alert('Вкажіть кількість упакованого');
      return;
    }
    setSaving(true);
    try {
      await packagingTaskService.complete(completeDialog.id, { quantity_packaged: v });
      setCompleteDialog(null);
      await loadData();
    } catch (e) {
      const d = e.response?.data;
      alert(d ? JSON.stringify(d, null, 2) : e.message || 'Помилка');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (task) => {
    if (!window.confirm('Скасувати задачу та повернути все зняте на склад напівфабрикатів?')) return;
    try {
      await packagingTaskService.cancel(task.id);
      await loadData();
    } catch (e) {
      const d = e.response?.data;
      alert(d ? JSON.stringify(d, null, 2) : e.message || 'Помилка');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  const inProgress = tasks.filter((t) => t.status === 'in_progress');
  const done = tasks.filter((t) => t.status !== 'in_progress');

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Упаковка
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Позиції зі складу напівфабрикатів. «Взяти в роботу» знімає залишок зі складу; після завершення
        упакована кількість з’являється в надходженнях обраного складу (готова продукція або вкладка
        «Надходження» на складі напівфабрикатів).
        {currentUser?.full_name ? (
          <> Виробіток за зміну фіксується на пакувальника: <strong>{currentUser.full_name}</strong>.</>
        ) : null}
      </Typography>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Заявки на упаковку (з планера)
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Виріб</TableCell>
              <TableCell>К-сть</TableCell>
              <TableCell>План старт</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Пакування</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">Немає заявок</Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{orderRefFromRow({ ...o, production_order_id: o.id })}</TableCell>
                  <TableCell>
                    {typeof o.product_name === 'string' && o.product_name.trim()
                      ? o.product_name
                      : o.product || '—'}
                  </TableCell>
                  <TableCell>{o.quantity}</TableCell>
                  <TableCell>
                    {o.planned_start ? String(o.planned_start).replace('T', ' ').slice(0, 16) : '—'}
                  </TableCell>
                  <TableCell>{o.status_display || o.status}</TableCell>
                  <TableCell>{o.packaging_note || '—'}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => openStartFromRequest(o)}
                    >
                      Взяти в роботу
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Склад для пакування (вироби)
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Артикул</TableCell>
              <TableCell>Виріб</TableCell>
              <TableCell>Кількість</TableCell>
              <TableCell>Од.</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {semiItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">Немає позицій з виробом на складі</Typography>
                </TableCell>
              </TableRow>
            ) : (
              semiItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.product_article || '—'}</TableCell>
                  <TableCell>{item.product_name || '—'}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={item.quantity < 0.01}
                      onClick={() => openStart(item)}
                    >
                      Взяти в роботу
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Задачі упаковки
      </Typography>
      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>№</TableCell>
              <TableCell>Виріб</TableCell>
              <TableCell>Пакувальник</TableCell>
              <TableCell>Пакування (план)</TableCell>
              <TableCell>Упаковка (факт)</TableCell>
              <TableCell>Знято</TableCell>
              <TableCell>Упаковано</TableCell>
              <TableCell>Склад призначення</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...inProgress, ...done].length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary">Немає задач</Typography>
                </TableCell>
              </TableRow>
            ) : (
              [...inProgress, ...done].map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>
                    {formatArticleName(t.product_article, t.product_name)}
                  </TableCell>
                  <TableCell>{t.packer_name || '—'}</TableCell>
                  <TableCell>{t.production_order_packaging_note || '—'}</TableCell>
                  <TableCell>
                    {Array.isArray(t.packaging_used_rows) && t.packaging_used_rows.length > 0
                      ? t.packaging_used_rows
                          .map((r) => {
                            const name = r.packaging_material_name || String(r.stock_item || '');
                            const q = Number(r.quantity_used || 0);
                            const u = r.unit || '';
                            return `${name}: ${q.toFixed(2)}${u ? ` ${u}` : ''}`;
                          })
                          .join('; ')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {t.quantity_taken}{' '}
                    {t.source_stock_item_data?.unit || ''}
                  </TableCell>
                  <TableCell>
                    {t.status === 'in_progress' ? (
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <TextField
                          size="small"
                          type="number"
                          sx={{ width: 120 }}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          defaultValue={
                            t.quantity_packaged != null ? t.quantity_packaged : ''
                          }
                          key={`pkg-${t.id}-${t.quantity_packaged ?? 'x'}`}
                          onBlur={(e) => {
                            const val = e.target.value;
                            if (val === '' || val == null) return;
                            savePackagedDraft(t, val);
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          збереження по виходу з поля
                        </Typography>
                      </Box>
                    ) : (
                      (t.quantity_packaged ?? '—')
                    )}
                  </TableCell>
                  <TableCell>{t.destination_warehouse}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t.status_display || t.status}
                      color={
                        t.status === 'in_progress'
                          ? 'warning'
                          : t.status === 'completed'
                          ? 'success'
                          : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    {t.status === 'in_progress' && (
                      <>
                        <Button size="small" sx={{ mr: 1 }} onClick={() => openComplete(t)}>
                          Завершити
                        </Button>
                        <Button size="small" color="error" onClick={() => handleCancel(t)}>
                          Скасувати
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={startOpen} onClose={() => !saving && setStartOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Взяти в роботу</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {selectedItem.production_order_packaging_note ? (
                <Typography variant="body2" color="text.secondary">
                  Пакування з планера: {selectedItem.production_order_packaging_note}
                </Typography>
              ) : null}
              <Typography variant="body2">
                {formatArticleName(selectedItem.product_article, selectedItem.product_name)} — на
                складі: {selectedItem.quantity} {selectedItem.unit}
              </Typography>
              <TextField
                label="Кількість зняти зі складу"
                type="number"
                fullWidth
                value={startQty}
                onChange={(e) => setStartQty(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
              />
              <TextField
                select
                label="Склад призначення (після упаковки)"
                fullWidth
                value={startWarehouse}
                onChange={(e) => setStartWarehouse(e.target.value)}
              >
                {DESTINATION_WAREHOUSES.map((w) => (
                  <MenuItem key={w} value={w}>
                    {w}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartOpen(false)} disabled={saving}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={handleStart} disabled={saving}>
            Почати
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(completeDialog)}
        onClose={() => !saving && setCompleteDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Завершити упаковку</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Буде створено надходження на склад «{completeDialog?.destination_warehouse}». Неупакований
            залишок повернеться на склад напівфабрикатів.
          </Typography>
          <TextField
            label="Кількість упакованого (факт)"
            type="number"
            fullWidth
            value={completeQty}
            onChange={(e) => setCompleteQty(e.target.value)}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialog(null)} disabled={saving}>
            Назад
          </Button>
          <Button variant="contained" onClick={handleComplete} disabled={saving}>
            Підтвердити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PackagingPage;
